import { map, values, sortBy, uniq } from 'lodash-es'
import { Segment } from 'redux/employer/employerTypes'
import { Month } from 'shared/DateUtils'
import moment from 'moment'
import wordings from './wordings.json'
import {
  grhType,
  qtfType,
  micromobilityType,
  flexType,
  ProgramsType,
  availableGrhMerchant,
  availableMicromobilityMerchant,
  availableFlexMerchant,
} from './types'
import { CardProgram } from 'redux/programs/programsTypes'
import { prepareSuperProgramForEditing } from '../Overview/helperFunctions'
import camelize from 'camelize'
import {
  applyLaunchGroupStatuses,
  mergeCardProgramsByLaunch,
  mergeProgramIntoLaunchGroup,
  getProgramScopeId,
} from './superProgramDisplay'

/*
  We want to consolidate programs as much as we can
  If they are created which launch a program, they will have a launch id
  all the same configuration with different segments
  so combining them by launch id

  Also adding different program types
*/
export const getSuperPrograms = (
  allCardPrograms: CardProgram[] | null,
  qtfPrograms: object,
  allSegments: Segment[],
  availableGrhMerchant: availableGrhMerchant,
  availableMicromobilityMerchant: availableMicromobilityMerchant,
  availableFlexMerchant: availableFlexMerchant,
  incompleteLaunch: boolean,
  draftState: string,
  allTags: { id: string; name: string }[] = []
) => {
  let programGroups = {}
  programGroups = mergeCardProgramsByLaunch(allCardPrograms, programGroups)
  programGroups = getAllPrograms(qtfPrograms, programGroups, 'qtf')
  programGroups = applyLaunchGroupStatuses(
    programGroups,
    allCardPrograms,
    qtfPrograms
  )

  const sortedValues = sortBy(values(programGroups), 'createdAt', 'desc')
  // Formatting the super programs and adding name
  let correctedSuperPrograms = sortedValues.map((superProgram) => ({
    ...prepareSuperProgramForEditing(
      superProgram,
      allSegments,
      availableGrhMerchant,
      availableMicromobilityMerchant,
      availableFlexMerchant,
      allTags
    ),
    name: superProgram.superProgramName || superProgram.name || 'Program',
  }))

  // Adding a draft program to the super programs
  if (incompleteLaunch) {
    let newStateJson = JSON.parse(draftState)
    newStateJson = camelize(newStateJson)
    correctedSuperPrograms = [
      {
        ...newStateJson,
        status: 'DRAFT',
        name: newStateJson.programName || 'Draft Program',
      },
      ...correctedSuperPrograms,
    ]
  }
  return correctedSuperPrograms
}

const getAllPrograms = (
  programState: object,
  programGroups: object,
  programType: string
) => {
  map(programState, (programObject) => {
    const active = programObject.active
    const nextMonth = programObject.nextMonth
    const programData = active || nextMonth
    if (!programData?.launchId) {
      return
    }
    mergeProgramIntoLaunchGroup(programGroups, programData, programType)
  })
  return programGroups
}

const segmentNameInList = (segment: Segment) => {
  return (
    segment.name +
    ` (${segment.employees.length - segment.contractors.length} eligible employees)`
  )
}

export { getProgramScopeId } from './superProgramDisplay'

export interface SegmentList {
  id: string
  text: string
  type: string
}

export const arrangeTagsForProgramSelection = (
  allTags: { id: string; name: string; commuters: number }[]
): SegmentList[] =>
  allTags.map((tag) => ({
    id: tag.id,
    text: `${tag.name} (${tag.commuters} eligible employees)`,
    type: 'TAG',
  }))

export const arrangeScopesForProgramSelection = (
  allSegments: Segment[],
  allTags: { id: string; name: string; commuters: number }[] = []
): SegmentList[] => [
  ...arrangeAllSegmentsForSelection(allSegments),
  ...arrangeTagsForProgramSelection(allTags),
]

export const getProgramScopePayload = (scope: SegmentList) =>
  scope.type === 'TAG' ? { tag: scope.id } : { segment: scope.id }

export const arrangeAllSegmentsForSelection = (
  allSegments: Segment[]
): SegmentList[] => {
  const segments = []
  // Not adding type all to the list because we have select all
  allSegments.forEach((segment) => {
    if (segment.type !== 'ALL') {
      segments.push({
        id: segment.id,
        text: segmentNameInList(segment),
        type: segment.type,
      })
    }
  })
  return segments
}

export const getAllEmployeeSegment = (allSegments: Segment[]) => {
  return allSegments.find((segment) => segment.type === 'ALL')
}

export const createAllSegmentsString = (
  selectedSegments: SegmentList[],
  createdProgramsForSegments: Set<string>
): string => {
  let segmentText = ''
  selectedSegments.forEach((segment) => {
    if (createdProgramsForSegments.has(segment.id)) {
      segmentText += segment.text + ', '
    }
  })
  return segmentText.slice(0, -2)
}

export const programsSelection = (
  id: ProgramsType,
  selectedPrograms: ProgramsType[]
) => {
  return selectedPrograms.includes(id)
    ? selectedPrograms.filter((program) => program !== id)
    : [...selectedPrograms, id]
}

export const getCreatedProgramsForSegments = (
  selectedSegments: SegmentList[],
  programState: object
): Set<string> => {
  const createdProgramsForSegments = new Set<string>()
  map(programState, (programObject) => {
    if (programObject.active || programObject.nextMonth) {
      const segmentId =
        programObject.active?.tag ||
        programObject.active?.segment ||
        programObject.nextMonth?.tag ||
        programObject.nextMonth?.segment
      if (selectedSegments.some((segment) => segment.id === segmentId)) {
        createdProgramsForSegments.add(segmentId)
      }
    }
  })
  return createdProgramsForSegments
}

export const checkIfSelectedSegmentsHaveCustomGroup = (
  selectedSegments: SegmentList[],
  allSegments: Segment[]
) => {
  return selectedSegments.some(
    (segment) => allSegments.find((s) => s.id === segment.id)?.type === 'CUSTOM'
  )
}

// We do not enable QTF past the 20th of the month
export const canCreateQtfForCurrentDate = (selectedDate: Month) => {
  const selectedMoment = moment(selectedDate.iso)
  const currentMoment = moment()

  // Get the next month's date
  const nextMonth = moment(currentMoment).add(1, 'month')

  // Check if selected date is specifically the next month
  const isNextMonth =
    selectedMoment.month() === nextMonth.month() &&
    selectedMoment.year() === nextMonth.year()

  // If we're after the 20th and trying to create for next month
  if (currentMoment.date() > 20 && isNextMonth) {
    return false
  }

  return true
}

export const getEstimatedMonthlySpend = (
  selectedPrograms: ProgramsType[],
  qtf: qtfType,
  grh: grhType,
  micromobility: micromobilityType,
  flex: flexType,
  transitPass?: { budget: number; walletTopUpEnabled?: boolean }
) => {
  let total = 0
  if (!selectedPrograms) {
    return 0
  }
  selectedPrograms.forEach((program) => {
    if (program === 'qtf') {
      total += qtf.benefits.transit ? qtf.employerContributions.transit : 0
      total += qtf.benefits.parking ? qtf.employerContributions.parking : 0
    }
    if (program === 'grh') {
      total += (grh.uses * grh.transaction) / 12
    }
    if (program === 'micromobility') {
      total += micromobility.budget
    }
    if (program === 'flex') {
      total += flex.budget
    }
    if (program === 'transitPass' && transitPass?.walletTopUpEnabled) {
      total += transitPass.budget
    }
  })
  return total
}

export const findProgramIdForSegment = (
  ids: {
    programId: string
    segmentId?: string
    tagId?: string
    scopeId?: string
  }[],
  segment: SegmentList
) => {
  return ids.find(
    (id) =>
      id.scopeId === segment.id ||
      id.segmentId === segment.id ||
      id.tagId === segment.id
  )?.programId
}

export const validateLaunchPrograms = (
  grh: grhType,
  qtf: qtfType,
  micromobility: micromobilityType,
  flex: flexType,
  transitPass: import('./types').transitPassType,
  selectedDate: Month,
  selectedPrograms: ProgramsType[],
  irsLimit: number,
  selectedSegments: SegmentList[],
  coupon?: import('./types').couponType,
  reportedUsage?: import('./types').reportedUsageType
) => {
  if (selectedSegments.length === 0) {
    return {
      isValid: false,
      message: wordings.disabledReasons.selectGroup,
      program: '',
    }
  }
  for (const program of selectedPrograms) {
    // QTF
    if (program === 'qtf') {
      // The employer contribution should not exceed the irs limit
      if (
        qtf.employerContributions.transit > irsLimit ||
        qtf.employerContributions.parking > irsLimit
      ) {
        return {
          isValid: false,
          message: wordings.finalValidations.qtf.crossedIrsLimit,
          heading: `Error in ${wordings[program].title}`,
        }
      }
      // There should be at least one selected transit or parking
      if (!qtf.benefits.transit && !qtf.benefits.parking) {
        return {
          isValid: false,
          message: wordings.finalValidations.qtf.noTransitOrParking,
          heading: `Error in ${wordings[program].title}`,
        }
      }
      // The finalizeAllocation day should not be less than current day
      // if selected date month is current month
      if (moment(selectedDate.iso).month() === moment().month()) {
        if (qtf.finalizeAllocation < moment().date()) {
          return {
            isValid: false,
            message:
              wordings.finalValidations.qtf
                .finalizeAllocationLessThanCurrentDay,
            heading: `Error in ${wordings[program].title}`,
          }
        }
      }
    }

    // GRH
    if (program === 'grh') {
      // Uses should be greater than 0
      if (grh.uses <= 0) {
        return {
          isValid: false,
          message: wordings.finalValidations.grh.usesLessThanZero,
          heading: `Error in ${wordings[program].title}`,
        }
      }
      // Transaction should be greater than 0
      if (grh.transaction <= 0) {
        return {
          isValid: false,
          message: wordings.finalValidations.grh.transactionLessThanZero,
          heading: `Error in ${wordings[program].title}`,
        }
      }
      // There should be at least one active rideshare or local taxi
      if (!grh.activeOptions.rideshare && !grh.activeOptions.localTaxi) {
        return {
          isValid: false,
          message: wordings.finalValidations.grh.noActiveRideshareOrLocalTaxi,
          heading: `Error in ${wordings[program].title}`,
        }
      }
    }

    // Micromobility
    if (program === 'micromobility') {
      // Budget should be greater than 0
      if (micromobility.budget <= 0) {
        return {
          isValid: false,
          message: wordings.finalValidations.micromobility.budgetLessThanZero,
          heading: `Error in ${wordings[program].title}`,
        }
      }
      // There should be at least one active leasing, shared, or one-time
      if (
        !micromobility.activeOptions.leasing &&
        !micromobility.activeOptions.shared &&
        !micromobility.activeOptions.oneTime
      ) {
        return {
          isValid: false,
          message: wordings.finalValidations.micromobility.noActiveOptions,
          heading: `Error in ${wordings[program].title}`,
        }
      }
      // For a active option there should be at least one active merchant
      if (
        micromobility.activeOptions.leasing &&
        micromobility.activeMerchantIds.leasing.length === 0
      ) {
        return {
          isValid: false,
          message: wordings.finalValidations.micromobility.noActiveMerchant,
          heading: `Error in ${wordings[program].title}`,
        }
      }
      if (
        micromobility.activeOptions.shared &&
        micromobility.activeMerchantIds.shared.length === 0
      ) {
        return {
          isValid: false,
          message: wordings.finalValidations.micromobility.noActiveMerchant,
          heading: `Error in ${wordings[program].title}`,
        }
      }
      if (
        micromobility.activeOptions.oneTime &&
        micromobility.activeMerchantIds.oneTime.length === 0
      ) {
        return {
          isValid: false,
          message: wordings.finalValidations.micromobility.noActiveMerchant,
          heading: `Error in ${wordings[program].title}`,
        }
      }
    }

    // Flex
    if (program === 'flex') {
      if (flex.budget <= 0) {
        return {
          isValid: false,
          message: wordings.finalValidations.flex.budgetLessThanZero,
          heading: `Error in ${wordings[program].title}`,
        }
      }
      if (
        !flex.activeOptions.flex &&
        !flex.activeOptions.gas &&
        !flex.activeOptions.transit &&
        !flex.activeOptions.parking &&
        !flex.activeOptions.tolls &&
        !flex.activeOptions.rideshare &&
        !flex.activeOptions.carshare &&
        !flex.activeOptions.transportationServices
      ) {
        return {
          isValid: false,
          message: wordings.finalValidations.flex.noActiveOptions,
          heading: `Error in ${wordings[program].title}`,
        }
      }
      if (flex.activeOptions.flex && flex.activeMerchantIds.flex.length === 0) {
        return {
          isValid: false,
          message: wordings.finalValidations.flex.noActiveMerchant,
          heading: `Error in ${wordings[program].title}`,
        }
      }
    }

    // Transit Pass
    if (program === 'transitPass') {
      if (
        transitPass.walletTopUpEnabled &&
        transitPass.budget <= 0
      ) {
        return {
          isValid: false,
          message: wordings.finalValidations.transitPass.budgetLessThanZero,
          heading: `Error in ${wordings[program].title}`,
        }
      }
      if (
        !transitPass.agenciesEnabled ||
        transitPass.transitAgencyIds.length === 0
      ) {
        return {
          isValid: false,
          message: wordings.finalValidations.transitPass.noActiveOptions,
          heading: `Error in ${wordings[program].title}`,
        }
      }
    }

    if (program === 'coupon') {
      if (
        !coupon?.merchantsEnabled ||
        !coupon.merchantIds ||
        !coupon.merchantIds.length
      ) {
        return {
          isValid: false,
          message: wordings.finalValidations.coupon.noActiveMerchant,
          heading: `Error in ${wordings[program].title}`,
        }
      }
      if (!coupon?.couponCode?.trim()) {
        return {
          isValid: false,
          message: wordings.finalValidations.coupon.noCouponCode,
          heading: `Error in ${wordings[program].title}`,
        }
      }
    }

    if (program === 'reportedUsage') {
      if (!reportedUsage?.merchants?.length) {
        return {
          isValid: false,
          message: wordings.finalValidations.reportedUsage.noMerchant,
          heading: `Error in ${wordings[program].title}`,
        }
      }
    }
  }
  return { isValid: true, message: '', program: selectedPrograms[0] }
}

export const hasMaxAllocationLimitExceeded = (
  qtf: qtfType,
  irsTaxLimit: number,
  selectedPrograms: ProgramsType[]
): boolean => {
  // Only check if QTF is selected
  if (!selectedPrograms.includes('qtf')) {
    return false
  }

  // Check transit max allocation limit
  if (qtf.benefits.transit) {
    const transitMaxLimit = irsTaxLimit - qtf.employerContributions.transit
    const transitDisplayValue =
      qtf.maxAllocationLimit.transit === null
        ? transitMaxLimit
        : qtf.maxAllocationLimit.transit

    if (transitDisplayValue > transitMaxLimit || transitDisplayValue < 0) {
      return true
    }
  }

  // Check parking max allocation limit
  if (qtf.benefits.parking) {
    const parkingMaxLimit = irsTaxLimit - qtf.employerContributions.parking
    const parkingDisplayValue =
      qtf.maxAllocationLimit.parking === null
        ? parkingMaxLimit
        : qtf.maxAllocationLimit.parking

    if (parkingDisplayValue > parkingMaxLimit || parkingDisplayValue < 0) {
      return true
    }
  }

  return false
}
